import { apiSlice } from "../../app/api/apiSlice";

const SHOP_URL = "/shop";

const buildQueryString = (params = {}) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === null || typeof value === "undefined" || value === "") return;
    search.set(key, value);
  });
  const query = search.toString();
  return query ? `?${query}` : "";
};

export const shopApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getShopAccess: builder.query({
      query: () => `${SHOP_URL}/access`,
      providesTags: ["ShopAccess"],
    }),

    getShopProducts: builder.query({
      query: () => `${SHOP_URL}/products`,
      providesTags: (result) =>
        result
          ? [
              ...result.map((product) => ({ type: "ShopProduct", id: product._id })),
              { type: "ShopProduct", id: "LIST" },
            ]
          : [{ type: "ShopProduct", id: "LIST" }],
    }),

    getShopProduct: builder.query({
      query: (id) => `${SHOP_URL}/products/${id}`,
      providesTags: (result, error, arg) => [{ type: "ShopProduct", id: arg }],
    }),

    createShopProduct: builder.mutation({
      query: (payload) => ({
        url: `${SHOP_URL}/products`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: [{ type: "ShopProduct", id: "LIST" }],
    }),

    updateShopProduct: builder.mutation({
      query: ({ id, ...payload }) => ({
        url: `${SHOP_URL}/products/${id}`,
        method: "PATCH",
        body: payload,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "ShopProduct", id: arg.id },
        { type: "ShopProduct", id: "LIST" },
      ],
    }),

    uploadShopProductImages: builder.mutation({
      query: (formData) => ({
        url: `${SHOP_URL}/products/images`,
        method: "POST",
        body: formData,
      }),
    }),

    reorderShopProducts: builder.mutation({
      query: ({ orderedIds }) => ({
        url: `${SHOP_URL}/products/reorder`,
        method: "PATCH",
        body: { orderedIds },
      }),
      invalidatesTags: [{ type: "ShopProduct", id: "LIST" }],
    }),

    deleteShopProduct: builder.mutation({
      query: (id) => ({
        url: `${SHOP_URL}/products/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "ShopProduct", id: arg },
        { type: "ShopProduct", id: "LIST" },
      ],
    }),

    createShopOrder: builder.mutation({
      query: (payload) => ({
        url: `${SHOP_URL}/orders`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: [
        { type: "ShopOrder", id: "LIST" },
        { type: "ShopProduct", id: "LIST" },
      ],
    }),

    getMyShopOrders: builder.query({
      query: () => `${SHOP_URL}/orders/my`,
      providesTags: (result) =>
        result
          ? [
              ...result.map((order) => ({ type: "ShopOrder", id: order._id })),
              { type: "ShopOrder", id: "LIST" },
            ]
          : [{ type: "ShopOrder", id: "LIST" }],
    }),

    cancelMyShopOrder: builder.mutation({
      query: (id) => ({
        url: `${SHOP_URL}/orders/${id}/my/cancel`,
        method: "PATCH",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "ShopOrder", id: arg },
        { type: "ShopOrder", id: "LIST" },
        { type: "ShopProduct", id: "LIST" },
      ],
    }),

    downloadMyShopOrderInvoice: builder.mutation({
      query: (id) => ({
        url: `${SHOP_URL}/orders/${id}/my/invoice`,
        responseHandler: async (response) => {
          const blob = await response.blob();
          return URL.createObjectURL(blob);
        },
      }),
    }),

    getAdminShopOrders: builder.query({
      query: ({ month, status, fulfillmentStatus } = {}) =>
        `${SHOP_URL}/orders/admin${buildQueryString({ month, status, fulfillmentStatus })}`,
      providesTags: (result) =>
        result
          ? [
              ...result.map((order) => ({ type: "ShopOrder", id: order._id })),
              { type: "ShopOrder", id: "LIST" },
            ]
          : [{ type: "ShopOrder", id: "LIST" }],
    }),

    updateShopOrderStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `${SHOP_URL}/orders/${id}/status`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "ShopOrder", id: arg.id },
        { type: "ShopOrder", id: "LIST" },
      ],
    }),

    updateOrderFulfillment: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `${SHOP_URL}/orders/${id}/fulfillment`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "ShopOrder", id: arg.id },
        { type: "ShopOrder", id: "LIST" },
      ],
    }),

    editShopOrder: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `${SHOP_URL}/orders/${id}/edit`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "ShopOrder", id: arg.id },
        { type: "ShopOrder", id: "LIST" },
        { type: "ShopProduct", id: "LIST" },
      ],
    }),

    deleteShopOrder: builder.mutation({
      query: (id) => ({
        url: `${SHOP_URL}/orders/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "ShopOrder", id: arg },
        { type: "ShopOrder", id: "LIST" },
      ],
    }),

    exportShopOrdersSepa: builder.mutation({
      query: (payload) => ({
        url: `${SHOP_URL}/orders/export-sepa`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: [{ type: "ShopOrder", id: "LIST" }],
    }),

    getShopMonthlyReport: builder.query({
      query: ({ month }) => `${SHOP_URL}/orders/report${buildQueryString({ month })}`,
      providesTags: [{ type: "ShopOrder", id: "REPORT" }],
    }),

    downloadShopMonthlyReport: builder.mutation({
      query: ({ month, format }) => ({
        url: `${SHOP_URL}/orders/report${buildQueryString({ month, format })}`,
        responseHandler: (response) => response.blob(),
      }),
    }),
  }),
});

export const {
  useGetShopAccessQuery,
  useGetShopProductsQuery,
  useGetShopProductQuery,
  useCreateShopProductMutation,
  useUpdateShopProductMutation,
  useUploadShopProductImagesMutation,
  useReorderShopProductsMutation,
  useDeleteShopProductMutation,
  useCreateShopOrderMutation,
  useGetMyShopOrdersQuery,
  useCancelMyShopOrderMutation,
  useDownloadMyShopOrderInvoiceMutation,
  useGetAdminShopOrdersQuery,
  useUpdateShopOrderStatusMutation,
  useUpdateOrderFulfillmentMutation,
  useEditShopOrderMutation,
  useDeleteShopOrderMutation,
  useExportShopOrdersSepaMutation,
  useGetShopMonthlyReportQuery,
  useDownloadShopMonthlyReportMutation,
} = shopApiSlice;
